import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, FilePlus2, Pencil, Ban, FileText, Wallet, Receipt, Sigma } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Money, StatusBadge, EmptyState, Pager, Spinner, StatCard, Field } from "@/components/common";
import SearchSelect from "@/components/SearchSelect";
import PeriodBar, { rangeFor } from "@/components/PeriodBar";
import ReasonDialog from "@/components/ReasonDialog";
import { salesVat } from "@/pages/SalesEntry";
import { api, formatErr } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney, withinEditWindow } from "@/lib/format";
import DateInput from "@/components/DateInput";
import { useAuth } from "@/context/AuthContext";

const PAGE = 500;
const PERIODS = ["today", "month", "last_month", "last3", "fy", "last_fy", "custom"];

const EditDialog = ({ row, onOpenChange, onSaved, rate }) => {
  const [f, setF] = useState(null);
  useEffect(() => { if (row) setF({ period_from: row.period_from, period_to: row.period_to, total_sales: String(row.total_sales), notes: row.notes || "", reason: "" }); }, [row]);
  if (!row || !f) return null;
  const { vat } = salesVat(f.total_sales, rate);
  const save = async (confirm = false) => {
    try {
      await api.put(`/sales/${row.id}`, { client_id: row.client_id, ...f, total_sales: Number(f.total_sales), confirm_overlap: confirm });
      toast.success("Sales entry corrected"); onSaved(); onOpenChange();
    } catch (e) {
      if (e.response?.status === 409 && !confirm && window.confirm("This period overlaps another sales entry. Save anyway?")) save(true);
      else toast.error(formatErr(e));
    }
  };
  return (
    <Dialog open={!!row} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="sales-edit-dialog">
        <DialogHeader><DialogTitle>Correct {row.record_no}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="From date"><DateInput value={f.period_from} onChange={(v) => setF({ ...f, period_from: v })} testid="sales-edit-from" /></Field>
          <Field label="To date"><DateInput value={f.period_to} onChange={(v) => setF({ ...f, period_to: v })} testid="sales-edit-to" /></Field>
          <Field label="Total sales (incl. VAT)"><Input type="number" value={f.total_sales} onChange={(e) => setF({ ...f, total_sales: e.target.value })} className="num text-right" data-testid="sales-edit-total" /></Field>
          <Field label={`VAT (${rate}%)`}><div className="h-10 px-3 flex items-center justify-end rounded-md bg-amber-50 border border-amber-200 num">{fmtMoney(vat)}</div></Field>
          <div className="col-span-2"><Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field></div>
          <div className="col-span-2"><Field label="Reason for correction" required><Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="sales-edit-reason" /></Field></div>
        </div>
        <DialogFooter><Button disabled={f.reason.trim().length < 3 || !(Number(f.total_sales) > 0)} onClick={() => save(false)} className="bg-emerald-800 hover:bg-emerald-900" data-testid="sales-edit-submit">Save correction</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function SalesHistory() {
  const { user, settings } = useAuth();
  const nav = useNavigate();
  const rate = Number(settings?.standard_vat_rate ?? 15);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [period, setPeriod] = useState("month");
  const [range, setRange] = useState(rangeFor("month"));
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [voiding, setVoiding] = useState(null);

  useEffect(() => { api.get("/clients").then(({ data: d }) => setClients(d)); }, []);
  const load = useCallback(async () => {
    if (!clientId) { setData(null); return; }
    const { data: d } = await api.get("/sales", { params: { client_id: clientId, date_from: range[0], date_to: range[1], page, page_size: PAGE } });
    setData(d);
  }, [clientId, range, page]);
  useEffect(() => { load(); }, [load]);

  const pick = (p) => { setPeriod(p); if (p !== "custom") setRange(rangeFor(p)); setPage(1); };
  const canEdit = (r) => r.record_status === "active" && (user.role === "super_admin" || (user.role === "staff" && settings?.staff_can_edit_own && r.created_by === user.id && withinEditWindow(r.created_at)));
  const client = clients.find((c) => c.id === clientId);

  return (
    <div>
      <PageHeader eyebrow="Sales" title="Sales history" subtitle="Select a client to view its sales entries. Periods overlapping the selected range are shown."
        actions={user.role !== "auditor" && <Button onClick={() => nav("/sales/new")} className="bg-emerald-800 hover:bg-emerald-900" data-testid="sales-add-button"><FilePlus2 className="w-4 h-4 mr-1.5" />Add sales entry</Button>} />
      <div className="panel p-4 mb-4 space-y-4 fade-up">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-[380px]">
            <div className="text-[11px] font-medium text-slate-600 mb-1 flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-emerald-700" />Client company <span className="text-red-500">*</span></div>
            <SearchSelect value={clientId} onChange={(v) => { setClientId(v); setPage(1); }} options={clients.map((c) => [c.id, `${c.company_name} · ${c.code}`])} placeholder="Type to search client…" testid="sales-filter-client" />
          </div>
          {client && <div className="text-xs text-slate-500 num pb-2">VAT {client.vat_number}</div>}
        </div>
        <PeriodBar periods={PERIODS} period={period} from={range[0]} to={range[1]} onPeriod={pick} onFrom={(v) => setRange([v, range[1]])} onTo={(v) => setRange([range[0], v])} />
      </div>
      {!clientId ? <div className="panel"><EmptyState title="Select a client to view sales history" subtitle="Default period is the current month." testid="sales-history-select-client" /></div> : !data ? <Spinner /> : (<>
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-4" data-testid="sales-history-summary">
          <StatCard label="Sales entries" value={data.sums.count} icon={FileText} testid="sales-summary-count" />
          <StatCard label="Total sales (incl. VAT)" value={fmtMoney(data.sums.total_sales)} hint="SAR" icon={Sigma} testid="sales-summary-total" delay={50} />
          <StatCard label="Sales VAT" value={fmtMoney(data.sums.vat_amount)} hint="SAR" icon={Receipt} tone="amber" testid="sales-summary-vat" delay={100} />
          <StatCard label="Net sales" value={fmtMoney(data.sums.net_sales)} hint="SAR" icon={Wallet} tone="slate" testid="sales-summary-net" delay={150} />
        </div>
        <div className="panel overflow-hidden fade-up">
          <div className="overflow-x-auto">
            {!data.items.length ? <EmptyState subtitle="No sales entries for this client in the selected period." /> : (
              <table className="w-full" data-testid="sales-history-table">
                <thead><tr>{["Record", "Period from", "Period to", "VAT %", "Total sales", "VAT", "Net sales", "Notes", "Status", "Entered", ""].map((h) => <th key={h} className={`th ${["Total sales", "VAT", "Net sales"].includes(h) ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
                <tbody>
                  {data.items.map((r, i) => (
                    <tr key={r.id} className={`row-hover ${r.record_status === "voided" ? "opacity-60" : ""}`} data-testid={`sales-row-${i}`}>
                      <td className="td num text-xs text-slate-500">{r.record_no}</td>
                      <td className="td num text-xs">{fmtDate(r.period_from)}</td>
                      <td className="td num text-xs">{fmtDate(r.period_to)}</td>
                      <td className="td num text-xs">{r.vat_rate}%</td>
                      <td className="td text-right"><Money value={r.total_sales} strong /></td>
                      <td className="td text-right"><Money value={r.vat_amount} /></td>
                      <td className="td text-right"><Money value={r.net_sales} /></td>
                      <td className="td text-xs text-slate-600 max-w-[200px] truncate" title={r.void_reason || r.notes}>{r.record_status === "voided" ? `Voided: ${r.void_reason}` : r.notes || "—"}</td>
                      <td className="td"><StatusBadge status={r.record_status} /></td>
                      <td className="td text-xs"><div>{r.created_by_name}</div><div className="num text-[11px] text-slate-400">{fmtDateTime(r.created_at)}{r.revision_count ? ` · ${r.revision_count} correction(s)` : ""}</div></td>
                      <td className="td text-right whitespace-nowrap">
                        {canEdit(r) && <Button size="sm" variant="ghost" onClick={() => setEdit(r)} data-testid={`sales-edit-${i}`}><Pencil className="w-4 h-4" /></Button>}
                        {user.role === "super_admin" && r.record_status === "active" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setVoiding(r)} data-testid={`sales-void-${i}`}><Ban className="w-4 h-4" /></Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {data.total > 0 && <Pager page={page} pageSize={PAGE} total={data.total} onPage={setPage} />}
        </div>
      </>)}
      <EditDialog row={edit} rate={rate} onOpenChange={() => setEdit(null)} onSaved={load} />
      <ReasonDialog open={!!voiding} onOpenChange={() => setVoiding(null)} title="Void sales entry?" destructive confirmLabel="Void" testid="sales-void-dialog"
        description="The entry stays in history marked as voided and is excluded from totals."
        onConfirm={async (reason) => { try { await api.post(`/sales/${voiding.id}/void`, { reason }); toast.success("Voided"); setVoiding(null); load(); } catch (e) { toast.error(formatErr(e)); } }} />
    </div>
  );
}
